import type { OpenPositionsPinpointProps } from '@redbrickmedia/brickster-shared/nextjs/components'
import { OpenPositionsPinpoint as OpenPositionsPinpointBase } from '@redbrickmedia/brickster-shared/nextjs/components'

import { Button } from '@/app/components/Button'

/**
 * OpenPositionsPinpoint Component
 *
 * Displays job postings from Pinpoint ATS with filtering capabilities.
 * Uses the brickster-shared component with Paved-specific styling.
 */
export default function OpenPositionsPinpoint(props: any) {
  const { block } = props

  const componentProps: OpenPositionsPinpointProps = {
    ...block,
    ButtonComponent: Button,
    showDivisionLogo: false,
    clickableCard: true,
    hideEmptyCategories: true,
    leadInHeaderNoResults: 'No current openings',
    leadInTextNoResults:
      'We’re not hiring right now, but we update this page often. Check back soon for new roles.',
  }

  const classNames = {
    // Lead-in section
    root: 'max-w-[1280px] mx-auto text-white-default',
    leadInContainer: 'mx-auto mb-9xl max-w-[800px] text-center md:mb-10xl',
    leadInHeader: 'size-header-h3 mb-8xl text-white-default',
    leadInText: 'size-body-md',

    // Grid container
    gridContainer: 'mt-9xl grid grid-cols-1 xl:grid-cols-12',

    // Sidebar filters
    sidebar: 'xl:col-span-3',
    sidebarLabel: 'size-body-sm font-semibold uppercase tracking-wide text-green-700',
    sidebarButton:
      'cursor-pointer rounded-xxs !bg-neutral-50 text-white-950 py-xs px-sm hover:!bg-purple-300 transition-colors',
    sidebarButtonActive: '',
    sidebarButtonText: 'size-button-sm',
    sidebarButtonsContainer:
      'mb-9xl flex flex-wrap items-start gap-md lg:flex-row xl:flex-col xl:mb-0',

    // Positions container
    positionsContainer: 'flex flex-col gap-6xl xl:col-span-9',
    categoryContainer: 'space-y-4xl',
    categoryHeader: 'mb-4xl flex w-full items-center gap-lg',
    categoryTitle: 'size-header-h6 text-neutral-50',
    categoryDivider: 'h-[2px] flex-1 bg-neutral-100',

    // Job cards
    jobCardsContainer: 'gap-4xl !flex-wrap',
    jobCard:
      'sm:!basis-[calc(50%-var(--spacing-4xl)/2)] sm:grow-0 sm:shrink-0 rounded-sm bg-white-default p-2xl transition-colors hover:bg-green-50 active:bg-green-200 cursor-pointer',
    jobTitle: 'size-header-h5 text-white-950 mb-2xl',
    jobLink:
      'inline-flex items-center gap-md text-white-950 font-semibold after:content-[""] after:inline-block after:w-[6px] after:h-[10px] after:translate-y-[1px] after:bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2IiBoZWlnaHQ9IjEwIiB2aWV3Qm94PSIwIDAgNiAxMCIgZmlsbD0ibm9uZSI+PHBhdGggZD0iTTAuMTg0MzggOS4xOTg3NUMwLjQyOTM4IDkuNDQzNzUgMC44MjQzOCA5LjQ0Mzc1IDEuMDY5MzggOS4xOTg3NUw1LjIyNDM4IDUuMDQzNzVDNS40MTkzOCA0Ljg0ODc1IDUuNDE5MzggNC41MzM3NSA1LjIyNDM4IDQuMzM4NzVMMS4wNjkzOCAwLjE4Mzc1QzAuODI0MzggLTAuMDYxMjUgMC40MjkzOCAtMC4wNjEyNSAwLjE4NDM4IDAuMTgzNzVDLTAuMDYwNjIwMSAwLjQyODc1IC0wLjA2MDYyMDEgMC44MjM3NSAwLjE4NDM4IDEuMDY4NzVMMy44MDQzOCA0LjY5Mzc1TDAuMTc5MzggOC4zMTg3NUMtMC4wNjA2MjAyIDguNTU4NzUgLTAuMDYwNjIwMSA4Ljk1ODc1IDAuMTg0MzggOS4xOTg3NVoiIGZpbGw9IiMwYTBhMGEiLz48L3N2Zz4=)] after:bg-contain after:bg-no-repeat after:bg-center',
    logoContainer: '',

    // Empty state
    empty: {
      positionsContainer: 'lg:!col-span-12 lg:!col-start-1',
      positionsInner: '!hidden',
      sidebar: '!hidden',
    },
    emptyState: 'hidden',

    // CTA section
    ctaContainer: 'mt-10xl rounded-lg bg-green-600 p-6xl text-center text-white-default md:p-9xl',
    ctaHeader: 'size-header-h4 mb-lg',
    ctaText: 'size-body-lg mb-4xl',
    ctaButton: '',
  }

  return (
    <div id="open-positions" className="bg-green-700 py-6xl md:py-12xl">
      <div className="content-container">
        <OpenPositionsPinpointBase {...componentProps} classNames={classNames} />
      </div>
    </div>
  )
}
